import { GetServerSideProps, NextPage } from "next";
import { useState, useEffect, useCallback, memo, useMemo } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { getVehicleStaticProps } from "@/app/utils/filereader";
import { BRAND_NAMES } from "@/app/utils/utils";
import type {
  ColorUIProps as ColorsProps,
  ModelUIProps as ModelProps,
  VehicleUIProps as VehicleProps,
  VersionUIProps as VersionProps,
} from "@/app/types/vehicle";
import CarModels from "@/app/components/CarModels/CarModels";
import CarModelMobile from "@/app/components/CarModels/CarModelsMobile";
import CarDatasheetMobile from "@/app/components/CarsComponents/CarDatasheet/CarDatasheetMobile";
import NavBar from "@/app/components/CarsComponents/CarDatasheet/NavBar/";
import DifferentialsComponent from "@/app/components/CarsComponents/DifferentialItem";
import DifferentialsMobile from "@/app/components/CarsComponents/DifferentialItem/DifferentialMobile";
import PicsSwipper from "@/app/components/CarsComponents/PicsSwipper";
import { BannerProvider } from "@/app/contexts/BannerContext";
import BannerSection from "@/app/components/UI/BannerSection";
import { fetchBanners } from "@/app/lib/getData";
import { usePrefetchImages } from "@/app/hooks/usePrefetchImages";
import dynamic from "next/dynamic";
import { debounce } from "lodash";
import {
  getVehicleData,
  isValidVehicle,
  getAllVehicles,
  formatVehicleDataForUI,
} from "@/app/services/vehicleService";
import CarConnect from "@/app/components/CarsComponents/CarConnect/";

interface CarPageProps {
  carroId: string;
  defaultModel: string | null;
  isValidCar: boolean;
  initialVehicleData: VehicleProps | null;
  bannerImages: string[];
  bannerImagesMobile: string[];
  bannerImagesTablet: string[];
  links: string[];
  calls: string[];
  contents: string[];
  defenseTypes: string[];
  title_contents: string[];
}

const DynamicCarDatasheet = dynamic(
      () => import("@/app/components/CarsComponents/CarDatasheet"),
      { loading: () => <p>Carregando...</p> }
);

const CarPage: NextPage<CarPageProps> = (props) => {
  const {
    carroId,
    defaultModel,
    isValidCar,
    initialVehicleData,
    bannerImages,
    bannerImagesMobile,
    bannerImagesTablet,
    links,
    calls,
    contents,
    defenseTypes,
  } = props;
  
  const router = useRouter();
  usePrefetchImages(initialVehicleData as VehicleProps);
  
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [vehicle, setVehicle] = useState<VehicleProps | undefined>(
        initialVehicleData || undefined
  );
  const [selectedModel, setSelectedModel] = useState<ModelProps>();
  const [selectedVersion, setSelectedVersion] = useState<VersionProps>();
  const [selectedColor, setSelectedColor] = useState<ColorsProps>();
  
  useEffect(() => {
    setLoading(true);
    setVehicle(initialVehicleData || undefined);
    setSelectedModel(undefined);
    setSelectedVersion(undefined);
    setSelectedColor(undefined);
  }, [carroId, initialVehicleData]);
  
  const controlNavbar = useCallback(
        debounce(() => {
          const currentScroll = window.scrollY;
          setShow(currentScroll <= lastScrollY);
          setLastScrollY(currentScroll);
        }, 200),
        [lastScrollY]
  );
  
  useEffect(() => {
    window.addEventListener("scroll", controlNavbar, { passive: true });
    return () => window.removeEventListener("scroll", controlNavbar);
  }, [controlNavbar]);
  
  useEffect(() => {
    if (!isValidCar) {
      router.push("/");
      return;
    }
    if (vehicle?.models) {
      const defaultSelectedModel = defaultModel
            ? vehicle.models.find((model) =>
                  model.name.toLowerCase().includes(defaultModel.toLowerCase())
            )
            : vehicle.models[0];
      
      if (defaultSelectedModel) {
        setSelectedModel(defaultSelectedModel);
        const defaultVersion = defaultSelectedModel.versions[0];
        if (defaultVersion) {
          setSelectedVersion(defaultVersion);
          if (defaultVersion.colors?.length) {
            setSelectedColor(defaultVersion.colors[0]);
          }
        }
      }
      setLoading(false);
    }
  }, [isValidCar, vehicle, defaultModel, router]);
  
  const handleModelChange = useCallback((model: ModelProps) => {
    setSelectedModel(model);
    const firstVersion = model.versions[0];
    if (firstVersion) {
      setSelectedVersion(firstVersion);
      if (firstVersion.colors?.length) {
        setSelectedColor(firstVersion.colors[0]);
      }
    }
  }, []);
  
  const handleVersionChange = useCallback((version: VersionProps) => {
    setSelectedVersion(version);
    if (version.colors?.length) {
      setSelectedColor(version.colors[0]);
    }
  }, []);
  
  // Usa useMemo para memoizar os componentes que não mudam frequentemente
  const MemoizedPicsSwipper = useMemo(() => memo(PicsSwipper), []);
  const MemoizedDifferentialsComponent = useMemo(
        () => memo(DifferentialsComponent),
        []
  );
  
  return (
        <div key={carroId}>
          {loading && (
                <div className="w-screen h-screen fixed bg-white/75 backdrop-blur-sm top-0 z-50 flex">
                  <Image
                        src={BRAND_NAMES.siteLogoMenu}
                        alt="Logo"
                        width={164}
                        height={52}
                        className="min-w-[164px] w-[20vw] m-auto animate-pulse"
                  />
                </div>
          )}
          
          <BannerProvider contents={contents} defenseTypes={defenseTypes}>
            <BannerSection
                  setChatbotOpen={() => {}}
                  setIsConditionsModalOpen={() => {}}
                  setConditionsContent={() => {}}
                  links={links}
                  calls={calls}
                  bannerImages={bannerImages}
                  bannerImagesMobile={bannerImagesMobile}
                  bannerImagesTablet={bannerImagesTablet}
            />
          </BannerProvider>
          
          <section id="visao-geral" className="max-lg:hidden bg-white">
            <NavBar
                  car={vehicle}
                  model={selectedModel}
                  version={selectedVersion}
                  color={selectedColor}
                  setModel={handleModelChange}
                  setVersion={handleVersionChange}
                  setColor={setSelectedColor}
                  show={show}
            />
            
            <DynamicCarDatasheet
                  car={vehicle}
                  model={selectedModel}
                  version={selectedVersion}
                  color={selectedColor}
                  setModel={handleModelChange}
                  setVersion={handleVersionChange}
                  setColor={setSelectedColor}
                  spriteUrl={selectedColor?.carrossel || ""}
            />
            <MemoizedPicsSwipper
                  externalImages={vehicle?.external || []}
                  internalImages={vehicle?.internal || []}
            />
            <section id="diferenciais" className="pt-20">
              <MemoizedDifferentialsComponent
                    car={vehicle}
                    model={selectedModel}
                    version={selectedVersion}
                    setModel={handleModelChange}
                    setVersion={handleVersionChange}
                    setColor={setSelectedColor}
              />
              
            </section>
            <CarConnect/>
            <CarModels />
          </section>
          
          <div className="lg:hidden">
            <CarDatasheetMobile
                  car={vehicle}
                  model={selectedModel}
                  version={selectedVersion}
                  color={selectedColor}
                  setModel={handleModelChange}
                  setVersion={handleVersionChange}
                  setColor={setSelectedColor}
            />
            <DifferentialsMobile
                  car={vehicle}
                  model={selectedModel}
                  version={selectedVersion}
                  setModel={handleModelChange}
                  setVersion={handleVersionChange}
                  setColor={setSelectedColor}
            />
            <CarConnect/>
            <CarModelMobile />
          </div>
        </div>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
                                                               params,
                                                               query,
                                                               resolvedUrl,
                                                             }) => {
  if (params?.carro === "favicon.ico") return { notFound: true };
  if (!params?.carro)
    return { redirect: { destination: "/", permanent: false } };
  
  const carroId = params.carro as string;
  const model = (query.model as string)?.toLowerCase() || null;
  const isValidCar = isValidVehicle(carroId);
  
  // Remove parâmetros da URL
  const cleanPath = `/${carroId}`.split("?")[0];
  
  if (!isValidCar) return { redirect: { destination: "/", permanent: false } };
  
  try {
    const [vehicleData, banners] = await Promise.all([
      getVehicleData(carroId),
      fetchBanners(),
    ]);
    
    // Formata os dados do veículo para o formato esperado pela UI
    const formattedVehicleData = vehicleData
          ? formatVehicleDataForUI(vehicleData)
          : null;
    
    const indexes = banners.calls
          .map((call: string, index: number) =>
                call
                      .split(",")
                      .map((item) => item.trim().split("?")[0]) // Remove parâmetros de cada item
                      .includes(cleanPath)
                      ? index
                      : -1
          )
          .filter((index: number) => index !== -1);
    
    const bannerProps = {
      bannerImages: indexes.map((i) => banners.bannerImages[i]).filter(Boolean),
      bannerImagesMobile: indexes
            .map((i) => banners.bannerImagesMobile[i])
            .filter(Boolean),
      bannerImagesTablet: indexes
            .map((i) => banners.bannerImagesTablet[i])
            .filter(Boolean),
      links: indexes.map((i) => banners.links[i]).filter(Boolean),
      calls: indexes.map(() => `/${carroId}`),
      contents: indexes.map((i) => banners.contents[i]).filter(Boolean),
      defenseTypes: indexes.map((i) => banners.defenseTypes[i]).filter(Boolean),
      title_contents: indexes
            .map((i) => banners.title_contents?.[i])
            .filter(Boolean),
    };
    
    return {
      props: {
        carroId,
        defaultModel: model,
        isValidCar,
        initialVehicleData: formattedVehicleData,
        ...bannerProps,
      },
    };
  } catch (error) {
    console.error("Erro ao carregar dados:", error);
    return {
      props: {
        carroId,
        defaultModel: null,
        isValidCar,
        initialVehicleData: null,
        bannerImages: [],
        bannerImagesMobile: [],
        bannerImagesTablet: [],
        links: [],
        calls: [],
        contents: [],
        defenseTypes: [],
        title_contents: [],
      },
    };
  }
};

export default CarPage;
